import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, AlertTriangle, ShieldCheck } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../components/context/AuthContext';

/**
 * Landing route the impersonation handshake opens in a new tab.
 *
 * Flow:
 *   /auth/impersonate?token=<short-lived user JWT>
 *
 * We swap the URL token into localStorage (as the regular user
 * `token` key, so the rest of the tenant app uses it as a normal user
 * session), then redirect to `/dashboard`. The downstream
 * `MainApp` shell will pick the token up via `AuthContext` and render
 * the impersonated user's experience exactly as they'd see it.
 *
 * Showing a clear "Authenticating…" interstitial avoids the half-second
 * flash where the user might wonder whether anything happened.
 */
export default function ImpersonateLandingPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { fetchUser } = useAuth();
  const [error, setError] = useState(null);

  useEffect(() => {
    const token = params.get('token');
    if (!token) {
      // eslint-disable-next-line
      setError('No impersonation token provided in URL.');
      return;
    }
    // Stash the token under the same key the regular user app uses so
    // the rest of the tenant code path Just Works. We also stamp a
    // small flag so the FE can render a permanent "🛡 Impersonating"
    // banner across the session.
    try {
      localStorage.setItem('token', token);
      localStorage.setItem('is_impersonated_session', '1');
      // Drop any cached user blob so AuthContext re-fetches /auth/me
      // against the new token instead of showing stale data for one
      // render frame.
      delete axios.defaults.headers.common['Authorization'];
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      // Force AuthContext to re-hydrate against the new token. Without
      // this the parent AuthProvider's one-shot mount-effect already
      // ran with the prior token (or no token), so `user` would stay
      // stale and downstream hooks like useBrowserMetadata would never
      // see the impersonated organization. fetchUser swaps it in.
      fetchUser?.();
      // Tiny delay so the user reads the success copy.
      const t = setTimeout(() => navigate('/dashboard', { replace: true }), 350);
      return () => clearTimeout(t);
    } catch (e) {
      // eslint-disable-next-line
      setError(`Could not store the impersonation token: ${e?.message || e}`);
    }
    // `fetchUser` is recreated on every AuthProvider render — including it
    // here would re-trigger this effect on every parent re-render and
    // continually clear/restart the 350ms navigate timeout, leaving us
    // stuck on the loading screen. Mount-only is intentional.
    // eslint-disable-next-line
  }, [params, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="max-w-md w-full bg-white border border-slate-200 rounded-xl shadow-sm p-8 text-center" data-testid="impersonate-landing">
        {error ? (
          <>
            <AlertTriangle className="w-10 h-10 text-red-500 mx-auto mb-3" />
            <h1 className="text-lg font-semibold text-slate-900 mb-1">Impersonation failed</h1>
            <p className="text-sm text-slate-600">{error}</p>
          </>
        ) : (
          <>
            <ShieldCheck className="w-10 h-10 text-emerald-500 mx-auto mb-3" />
            <h1 className="text-lg font-semibold text-slate-900 mb-1">Authenticating as target user…</h1>
            <p className="text-sm text-slate-500 mb-4">
              You will land on their dashboard in a moment. This session is logged
              for audit purposes.
            </p>
            <Loader2 className="w-5 h-5 text-slate-400 mx-auto animate-spin" />
          </>
        )}
      </div>
    </div>
  );
}
